// Artwork Intake API client. Routes and shapes per ARTWORK_INTAKE_PLAN.md §14.
//
// Admin methods use window.$A.Api (axios instance with AccessToken header).
// Public methods build a per-call axios instance carrying only EmbedToken.

const PUBLIC_TIMEOUT_MS = 30000;

function adminApi() {
  return window.$A.Api;
}

function publicApi(embedToken) {
  return window.$A.CreateApiInstance({ EmbedToken: embedToken }, PUBLIC_TIMEOUT_MS);
}

class ArtworkIntakeService {
  // ----- Admin -----

  async getAllSubmissions(filters = {}) {
    const params = {};
    if (filters.formId != null) params.formId = filters.formId;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    const res = await adminApi().get("/artwork-intake/submissions", { params });
    return res.data;
  }

  async getSubmission(id) {
    const res = await adminApi().get(`/artwork-intake/submissions/${id}`);
    return res.data;
  }

  async downloadSubmissionFilesZip(id) {
    const res = await adminApi().get(`/artwork-intake/submissions/${id}/files/zip`, {
      responseType: "blob",
      timeout: 0,
    });
    const blob = new Blob([res.data], { type: "application/zip" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `submission_${id}_files.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async updateSubmissionStatus(id, status) {
    const res = await adminApi().put(`/artwork-intake/submissions/${id}/status`, { status });
    return res.data;
  }

  async convertSubmissionToJob(id, jobRequest) {
    const res = await adminApi().post(
      `/artwork-intake/submissions/${id}/convert-to-job`,
      { job: jobRequest }
    );
    return res.data;
  }

  async convertSubmissionToProof(id, proofId) {
    const res = await adminApi().post(
      `/artwork-intake/submissions/${id}/convert-to-proof`,
      { proofId }
    );
    return res.data;
  }

  async getFormSettings() {
    const res = await adminApi().get("/artwork-intake/settings");
    return res.data;
  }

  async saveFormSettings(settings) {
    const body = {
      selectedCustomFieldIds: settings.selectedCustomFieldIds || [],
      requiredCustomFieldIds: settings.requiredCustomFieldIds || [],
      requireFile: !!settings.requireFile,
      emailRecipients: (settings.emailRecipients || []).map((r) =>
        r.userId != null ? { userId: r.userId } : { teamId: r.teamId }
      ),
    };
    if (Object.prototype.hasOwnProperty.call(settings, "teamId")) {
      body.teamId = settings.teamId;
    }
    if (settings.reminderFrequency != null) body.reminderFrequency = settings.reminderFrequency;
    if (settings.reminderTimeOfDay != null) body.reminderTimeOfDay = settings.reminderTimeOfDay;
    if (settings.reminderTimeZone != null) body.reminderTimeZone = settings.reminderTimeZone;
    const res = await adminApi().put("/artwork-intake/settings", body);
    return res.data;
  }

  // ----- Public (EmbedToken header) -----

  async fetchEmbedToken(formId) {
    const apiHost = window.$A.Env.apiHost.replace(/\/+$/, "");
    const res = await fetch(
      `${apiHost}/artwork-intake-form/embed.js?formId=${encodeURIComponent(formId)}`
    );
    if (!res.ok) return null;
    const text = await res.text();
    const m = text.match(/var\s+EMBED_TOKEN\s*=\s*'([^']+)'/);
    return m ? m[1] : null;
  }

  async getPublicFormConfig(formId, embedToken) {
    try {
      const res = await publicApi(embedToken).get(`/artwork-intake-form/${formId}`);
      return res.data;
    } catch (e) {
      if (e && e.response && (e.response.status === 404 || e.response.status === 410)) {
        return null;
      }
      throw e;
    }
  }

  async presignUpload(formId, payload, embedToken) {
    const res = await publicApi(embedToken).post(
      `/artwork-intake-form/${formId}/presign-upload`,
      payload
    );
    return res.data;
  }

  async submitForm(formId, payload, embedToken) {
    const res = await publicApi(embedToken).post(
      `/artwork-intake-form/${formId}/submit`,
      payload
    );
    return res.data;
  }
}

export default ArtworkIntakeService;
